import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import icone from '@/assets/icone.png';
import { SITE_URL } from '@/lib/siteUrl';
import PasswordInput, { passwordScore } from '@/components/PasswordInput';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('mode') === 'login' ? 'login' : 'signup';
  const selectedPlan = searchParams.get('plan');

  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupVoucher, setSignupVoucher] = useState('');
  const [signupDone, setSignupDone] = useState(false);
  const [signupEmailSent, setSignupEmailSent] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);


  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${SITE_URL}/reset-password`,
      });
      if (error) throw error;
      setForgotSent(true);
      toast({
        title: 'E-mail enviado',
        description: 'Enviamos um link para você criar uma nova senha.',
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao enviar e-mail',
        description: error.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (error) throw error;

      if (selectedPlan && selectedPlan !== 'start') {
        localStorage.setItem('pending_plan', selectedPlan);
      }

      toast({ title: 'Login efetuado!', description: 'Redirecionando...' });
      navigate('/dashboard');
    } catch (error: any) {
      const msg = String(error?.message || '').toLowerCase();
      const notConfirmed = msg.includes('not confirmed') || msg.includes('email_not_confirmed');
      toast({
        title: notConfirmed ? 'E-mail não confirmado' : 'Erro no login',
        description: notConfirmed
          ? 'Confirme seu e-mail pelo link que enviamos antes de entrar.'
          : 'E-mail ou senha incorretos.',
        variant: 'destructive',
      });

    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: signupEmail,
        password: signupPassword,
        options: {
          data: {
            nome: signupName,
            papel: 'coordenador',
            pending_plan: selectedPlan || 'start',
          },
          emailRedirectTo: `${SITE_URL}/dashboard`,
        },
      });

      if (error) throw error;

      if (selectedPlan && selectedPlan !== 'start') {
        localStorage.setItem('pending_plan', selectedPlan);
      }
      if (signupVoucher.trim()) {
        localStorage.setItem('pending_voucher', signupVoucher.trim().toUpperCase());
      }

      const hasSession = !!data.session;
      setSignupEmailSent(signupEmail);

      setSignupEmail('');
      setSignupPassword('');
      setSignupName('');
      setSignupVoucher('');


      if (hasSession) {
        toast({
          title: 'Cadastro realizado!',
          description: 'Conta criada! Vamos cadastrar sua empresa.',
        });
        navigate('/briefing');
      } else {
        setSignupDone(true);
        toast({
          title: 'Confirme seu e-mail',
          description: 'Enviamos um link de confirmação. Confirme para liberar seu acesso.',
        });
      }

    } catch (error: any) {
      toast({
        title: 'Erro no cadastro',
        description: error.message || 'Tente novamente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };


  const getPlanLabel = () => {
    if (!selectedPlan || selectedPlan === 'start') return null;
    if (selectedPlan === 'expert') return 'Plano Expert - R$ 29,90/mês';
    if (selectedPlan === 'max_pro') return 'Plano Max Pro - R$ 19,90/usuário/mês';
    return null;
  };

  const planLabel = getPlanLabel();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 border-border bg-card animate-scale-in">
        <div className="flex flex-col items-center justify-center gap-3 mb-8">
          <Link to="/">
            <img src={icone} alt="Bianchim Business" className="h-16 w-auto hover:scale-105 transition-transform" />
          </Link>
          <h1 className="text-xl font-bold text-foreground">Bianchim Business</h1>
          <p className="text-sm text-muted-foreground">Treinamento de Vendas com IA</p>

          {planLabel && (
            <div className="mt-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-sm font-medium text-primary">{planLabel}</p>
            </div>
          )}
        </div>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-muted">
            <TabsTrigger value="login" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Login</TabsTrigger>
            <TabsTrigger value="signup" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Cadastro</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            {forgotMode ? (
              forgotSent ? (
                <div className="space-y-4 text-center">
                  <h2 className="text-lg font-semibold text-foreground">Verifique seu e-mail</h2>
                  <p className="text-sm text-muted-foreground">
                    Enviamos um link para <strong className="text-foreground">{forgotEmail}</strong>.
                    Clique nele para criar uma nova senha.
                  </p>
                  <p className="text-xs text-muted-foreground">Não encontrou? Veja o spam ou promoções.</p>
                  <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={() => { setForgotMode(false); setForgotSent(false); }}>
                    ← Voltar para login
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Informe seu e-mail e enviaremos um link para você criar uma nova senha.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-foreground">E-mail</Label>
                    <Input id="forgot-email" type="email" placeholder="seu@email.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required className="bg-input border-border focus:border-primary" />
                  </div>
                  <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" disabled={loading}>
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</> : 'Enviar link de recuperação'}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={() => setForgotMode(false)}>
                    ← Voltar para login
                  </Button>
                </form>
              )
            ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-foreground">E-mail</Label>
                <Input id="login-email" type="email" placeholder="seu@email.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required className="bg-input border-border focus:border-primary" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-foreground">Senha</Label>
                <PasswordInput
                  id="login-password"
                  value={loginPassword}
                  onChange={setLoginPassword}
                  required
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-glow" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Entrando...</> : 'Entrar'}
              </Button>
              <Button type="button" variant="link" className="w-full text-muted-foreground hover:text-primary" onClick={() => { setForgotMode(true); setForgotEmail(loginEmail); }}>
                Esqueci minha senha
              </Button>
            </form>
            )}
          </TabsContent>


          <TabsContent value="signup">
            {signupDone ? (
              <div className="space-y-4 text-center">
                <h2 className="text-lg font-semibold text-foreground">Confirme seu e-mail</h2>
                <p className="text-sm text-muted-foreground">
                  Enviamos um link de confirmação para <strong className="text-foreground">{signupEmailSent}</strong>.
                  Abra seu e-mail e clique no link para liberar seu acesso.
                </p>
                <p className="text-xs text-muted-foreground">
                  Não encontrou? Verifique a caixa de spam ou promoções.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    setLoading(true);
                    const { error } = await supabase.auth.resend({
                      type: 'signup',
                      email: signupEmailSent,
                      options: { emailRedirectTo: `${SITE_URL}/dashboard` },
                    });
                    setLoading(false);
                    toast({
                      title: error ? 'Erro ao reenviar' : 'E-mail reenviado',
                      description: error ? error.message : 'Confira sua caixa de entrada.',
                      variant: error ? 'destructive' : 'default',
                    });
                  }}
                  disabled={loading}
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reenviando...</> : 'Reenviar e-mail de confirmação'}
                </Button>
                <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={() => setSignupDone(false)}>
                  Usar outro e-mail
                </Button>
              </div>
            ) : (
            <form onSubmit={handleSignup} className="space-y-4">

              <div className="space-y-2">
                <Label htmlFor="signup-name" className="text-foreground">Nome</Label>
                <Input id="signup-name" type="text" placeholder="Seu nome" value={signupName} onChange={(e) => setSignupName(e.target.value)} required className="bg-input border-border focus:border-primary" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-email" className="text-foreground">E-mail</Label>
                <Input id="signup-email" type="email" placeholder="seu@email.com" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required className="bg-input border-border focus:border-primary" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-password" className="text-foreground">Senha</Label>
                <PasswordInput
                  id="signup-password"
                  value={signupPassword}
                  onChange={setSignupPassword}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  showStrength
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-voucher" className="text-foreground">Voucher (opcional)</Label>
                <Input
                  id="signup-voucher"
                  type="text"
                  placeholder="Ex: BIANCHIM2026"
                  value={signupVoucher}
                  onChange={(e) => setSignupVoucher(e.target.value.toUpperCase())}
                  className="bg-input border-border focus:border-primary uppercase"
                />
                <p className="text-xs text-muted-foreground">
                  Tem um voucher? Informe aqui para liberar seu plano. Sem voucher, você começa no plano gratuito.
                </p>
              </div>

              <Button type="submit" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cadastrando...</> : 'Criar Conta'}
              </Button>
            </form>
            )}

          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-muted-foreground hover:text-primary">
            ← Voltar para o início
          </Button>
        </div>
      </Card>

    </div>
  );
};

export default Auth;
